/**
 * The plan as a mind map, and an editor for it.
 *
 * The same rows as the Gantt, read from the middle outwards: the project in the centre,
 * its phases fanned to either side, the work under each phase stacked beside it. It is
 * how a plan gets talked into existence in a workshop — somebody at a whiteboard adding
 * a branch every time the room names a piece of work — so it edits the way a mind map
 * editor does: Enter for a sibling, Tab for a child, F2 or a double-click to rename,
 * drag a topic onto another to move it there, a small circle on the outer edge to fold
 * a branch shut. Every edit is the same call the grid makes, so the Gantt, the sheet and
 * the breakdown show it the moment it lands.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import clsx from 'clsx'
import { Link } from '../../lib/router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { Button, ErrorNote, Spinner, useToast } from '../../components/ui'
import { PageHeader } from '../../components/DataTable'
import {
  AddIcon,
  ExportIcon,
  FitIcon,
  InsertBelowIcon,
  OpenIcon,
  RedoIcon,
  TickIcon,
  TrashIcon,
  UndoIcon,
  ZoomInIcon,
  ZoomOutIcon,
} from '../../components/icons'
import { Divider, Tip, Tool } from './Toolbar'
import { ContextMenu, type MenuEntry } from './ContextMenu'
import { phaseColours } from './Gantt'
import {
  placeOf,
  planRows,
  redoStepFor,
  selectOnFocus,
  usePlan,
  usePlanEdits,
  useUndo,
  type Row,
  type UndoStep, useShowClosed } from './plan'
import {
  ROOT_KEY,
  connector,
  descendants,
  dropTarget,
  lastOfSubtree,
  layoutMindMap,
  neighbour,
  sizeFor,
  toSvg,
  type MindNode,
  type Offsets,
} from './mindmap-layout'

type View = { x: number; y: number; k: number }

/** Which branches are folded shut, remembered per plan. */
function useCollapsed(scope: string): [Set<string>, (f: (prev: Set<string>) => Set<string>) => void] {
  const store = `furi.plan.mindmap.folded.${scope}`
  const [collapsed, setState] = useState<Set<string>>(() => {
    try {
      const saved = localStorage.getItem(store)
      const parsed: unknown = saved ? JSON.parse(saved) : null
      return new Set(Array.isArray(parsed) ? parsed.filter((k): k is string => typeof k === 'string') : [])
    } catch {
      return new Set()
    }
  })
  const set = useCallback(
    (f: (prev: Set<string>) => Set<string>) =>
      setState((prev) => {
        const next = f(prev)
        try {
          localStorage.setItem(store, JSON.stringify([...next]))
        } catch {
          // Forgetting the folds is not a reason to refuse them.
        }
        return next
      }),
    [store],
  )
  return [collapsed, set]
}

export function MindMapPage({
  scopeTo,
  onOpenTask,
}: {
  scopeTo: string
  /** See `GanttPage` — both pointed at a route this app does not have. */
  onOpenTask?: (taskId: string) => void
}) {
  const toast = useToast()
  const [showClosed, setShowClosed] = useShowClosed(scopeTo)
  const plan = usePlan(scopeTo, showClosed)
  const edits = usePlanEdits(scopeTo)
  /**
   * FROM THIS PROJECT, NOT FROM EVERY COMPANY AT ONCE.
   *
   * `writable` used to come from the kit's own permission hook, which answered yes if any
   * company the person belonged to granted `work.write` at anything other than `none`. A plain
   * client holds it at `own`, so every customer got an editable mind map — with "Mark done" and
   * "Delete" on every branch — while the board next door showed them the same work locked.
   *
   * This is the shape of the whole screen: whether ADDING a topic is on the menu at all. What
   * may be done to a row that already exists is per row, and the API says so per row.
   */
  const writable = (plan.data?.capabilities['work.write'] ?? 'none') !== 'none'
  /**
   * WHETHER NEW TOPICS MAY BE ADDED HERE AT ALL — asked by SIDE, not by capability.
   *
   * A customer's lead holds `work.write` at full, so `writable` says yes to them, and this map
   * was a way to type work straight onto our plan with none of the questions "Report it" asks.
   * Editing what is already here is a different question and stays on `writable` plus the row.
   */
  const mayAdd = writable && !plan.data?.customer
  /**
   * AND THE TWO THAT ARE NEVER A CUSTOMER'S, so the tools go rather than sit greyed.
   *
   * A disabled control says "not yet — pick a row"; an absent one says "not yours". Showing a
   * permanently dead bin to somebody who will never hold `work.delete` is the first of those
   * answering the second question, and the Gantt beside this already makes the distinction.
   */
  const mayDelete = (plan.data?.capabilities['work.delete'] ?? 'none') !== 'none'
  const mayTick = (plan.data?.capabilities['work.move'] ?? 'none') !== 'none'

  const rows = useMemo(() => planRows(plan.data), [plan.data])
  const [collapsed, setCollapsed] = useCollapsed(scopeTo)
  const arrangement = useArrangement(plan.data?.project.id)
  const layout = useMemo(
    () =>
      layoutMindMap(rows, {
        collapsed,
        projectName: plan.data?.project.name ?? 'Project',
        offsets: arrangement.offsets,
      }),
    [rows, collapsed, plan.data?.project.name, arrangement.offsets],
  )

  /**
   * What the two fold buttons can actually act on. Kept as values rather than asked inside the
   * handlers so the buttons can disable themselves on the same facts they would act on — a
   * button that is enabled and does nothing is the bug these replaced.
   */
  const phaseKeys = useMemo(
    () => rows.filter((r) => r.has_children && r.depth >= (layout.byKey.get(ROOT_KEY) ? 0 : 1)).map((r) => r.key),
    [rows, layout],
  )
  const folds = useMemo(() => [...collapsed], [collapsed])
  const colours = useMemo(() => phaseColours(rows), [rows])

  const [selected, setSelected] = useState<string | null>(null)
  const [editing, setEditing] = useState<string | null>(null)
  const [pendingEdit, setPendingEdit] = useState<string | null>(null)
  const [menu, setMenu] = useState<{ key: string; x: number; y: number } | null>(null)
  const [view, setView] = useState<View>({ x: 0, y: 0, k: 1 })
  const fitted = useRef(false)
  const canvasRef = useRef<HTMLDivElement>(null)
  const cardRef = useRef<HTMLDivElement>(null)

  // Undo, the way the Gantt does it: every edit records what puts it back.
  const [history, setHistory] = useState<UndoStep[]>([])
  const [redoStack, setRedo] = useState<UndoStep[]>([])
  const undo = useUndo(scopeTo, () => {})
  const record = useCallback((step: UndoStep) => {
    setHistory((h) => [...h.slice(-49), step])
    setRedo([])
  }, [])


  const fit = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas || !layout.width) return
    const W = canvas.clientWidth
    const H = canvas.clientHeight
    // The whole map when it fits legibly; otherwise readable, centred on the middle,
    // and the rest is a pan away. A map of two hundred tasks at eighteen per cent is a
    // picture of lines, not something anyone can work on.
    const whole = Math.min(1.4, (W - 32) / layout.width, (H - 32) / layout.height)
    const k = Math.max(0.45, whole)
    const root = layout.byKey.get(layout.root)
    if (whole >= 0.45 || !root) {
      setView({ x: (W - layout.width * k) / 2, y: (H - layout.height * k) / 2, k })
    } else {
      setView({ x: W / 2 - root.cx * k, y: H / 2 - root.cy * k, k })
    }
  }, [layout.width, layout.height])

  // Fit once the plan is here; after that the view is the person's, not ours.
  useEffect(() => {
    if (!fitted.current && layout.nodes.length >= 1 && canvasRef.current?.clientWidth) {
      fitted.current = true
      fit()
    }
  }, [layout.nodes.length, fit])

  // A topic added a moment ago opens for renaming the moment it appears.
  useEffect(() => {
    if (pendingEdit && layout.byKey.has(pendingEdit)) {
      setSelected(pendingEdit)
      setEditing(pendingEdit)
      setPendingEdit(null)
    }
  }, [pendingEdit, layout])

  const zoomBy = useCallback((factor: number, about?: { x: number; y: number }) => {
    setView((v) => {
      const k = Math.max(0.2, Math.min(3, v.k * factor))
      const canvas = canvasRef.current
      const cx = about?.x ?? (canvas?.clientWidth ?? 0) / 2
      const cy = about?.y ?? (canvas?.clientHeight ?? 0) / 2
      // Keep the point under the pointer where it is.
      const px = (cx - v.x) / v.k
      const py = (cy - v.y) / v.k
      return { x: cx - px * k, y: cy - py * k, k }
    })
  }, [])

  /* ------------------------------------------------------------- the edits */

  const rowOf = useCallback((key: string) => rows.find((r) => r.key === key), [rows])

  const addChild = useCallback(
    async (key: string) => {
      const node = layout.byKey.get(key)
      if (!node) return
      const isRoot = node.row === null
      const after = isRoot ? (rows.at(-1)?.key ?? null) : lastOfSubtree(rows, key)
      const made = await edits.add.mutateAsync({
        title: 'New task',
        after: after ?? undefined,
        ...(isRoot ? { atTopLevel: true } : { parent: key }),
      })
      if (node.collapsed) setCollapsed((c) => new Set([...c].filter((k) => k !== key)))
      record([{ kind: 'remove', key: made.key }])
      setPendingEdit(made.key)
    },
    [layout, rows, edits.add, record, setCollapsed],
  )

  const addSibling = useCallback(
    async (key: string) => {
      const node = layout.byKey.get(key)
      if (!node) return
      if (node.row === null) return addChild(key)
      const parent = node.parent ? layout.byKey.get(node.parent) : undefined
      const after = lastOfSubtree(rows, key)
      // A loose topic hangs off the centre on the screen but has no parent on the plan,
      // so what goes beside it is another top-level row.
      const topLevel = node.loose || !parent || !parent.row
      const made = await edits.add.mutateAsync({
        title: 'New task',
        after: after ?? undefined,
        ...(topLevel ? { atTopLevel: true } : { parent: parent.key }),
      })
      record([{ kind: 'remove', key: made.key }])
      setPendingEdit(made.key)
    },
    [layout, rows, edits.add, record, addChild],
  )

  const rename = useCallback(
    async (key: string, title: string) => {
      const row = rowOf(key)
      setEditing(null)
      // The input is about to go; the keys should keep working on the map, so Enter can
      // add the next topic without a click in between.
      canvasRef.current?.focus()
      if (!row || !title.trim() || title.trim() === row.title) return
      await edits.amend.mutateAsync({ key, body: { title: title.trim() } })
      record([{ kind: 'patch', key, body: { title: row.title } }])
    },
    [rowOf, edits.amend, record],
  )

  const remove = useCallback(
    (key: string) => {
      const node = layout.byKey.get(key)
      if (!node?.row) return
      if (node.children.length || node.hidden) {
        toast(`${node.title} has work under it. Move or remove that first.`, 'danger')
        return
      }
      const next = node.parent ?? null
      edits.remove.mutate(key, { onSuccess: () => setSelected(next) })
    },
    [layout, edits.remove, toast],
  )

  const moveUnder = useCallback(
    async (key: string, targetKey: string) => {
      const row = rowOf(key)
      const target = layout.byKey.get(targetKey)
      if (!row || !target) return
      const was = placeOf(row, rows)
      const toRoot = target.row === null
      const after = toRoot ? (rows.filter((r) => r.key !== key).at(-1)?.key ?? null) : lastOfSubtree(rows, targetKey)
      await edits.reorder.mutateAsync({
        key,
        after: after === key ? was.after : after,
        parent: toRoot ? null : targetKey,
        reparent: true,
      })
      record([{ kind: 'place', key, after: was.after, parent: was.parent }])
      if (target.collapsed) setCollapsed((c) => new Set([...c].filter((k) => k !== targetKey)))
    },
    [rowOf, layout, rows, edits.reorder, record, setCollapsed],
  )

  const toggle = useCallback(
    (key: string) =>
      setCollapsed((c) => {
        const next = new Set(c)
        if (next.has(key)) next.delete(key)
        else next.add(key)
        return next
      }),
    [setCollapsed],
  )

  /**
   * Ticking a topic off. It briefly asked what fixed it, because the API refused without a note;
   * that demand has gone — see `TestPanel` for why it asked the wrong person.
   */
  const tick = useCallback(
    (key: string) => {
      const row = rowOf(key)
      if (!row || row.has_children) return
      const done = row.status_category === 'done'
      edits.tick.mutate({ key, done: !done })
      record([{ kind: 'tick', key, done }])
    },
    [rowOf, edits.tick, record],
  )

  const undoLast = useCallback(async () => {
    const step = history.at(-1)
    if (!step) return
    const back = redoStepFor(step, rows)
    setHistory((h) => h.slice(0, -1))
    await undo.mutateAsync(step)
    setRedo((r) => [...r, back])
  }, [history, rows, undo])

  const redoLast = useCallback(async () => {
    const step = redoStack.at(-1)
    if (!step) return
    const back = redoStepFor(step, rows)
    setRedo((r) => r.slice(0, -1))
    await undo.mutateAsync(step)
    setHistory((h) => [...h, back])
  }, [redoStack, rows, undo])

  const exportMap = useCallback(
    async (kind: 'svg' | 'png') => {
      const svg = toSvg(layout, colours, { title: plan.data?.project.name ?? 'Plan' })
      const name = `${plan.data?.project.key ?? 'plan'}-mind-map`
      const save = (blob: Blob, ext: string) => {
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `${name}.${ext}`
        a.click()
        setTimeout(() => URL.revokeObjectURL(url), 1000)
      }
      if (kind === 'svg') {
        save(new Blob([svg], { type: 'image/svg+xml' }), 'svg')
        return
      }
      const img = new Image()
      const url = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' }))
      img.onload = () => {
        const scale = 2
        const canvas = document.createElement('canvas')
        canvas.width = layout.width * scale
        canvas.height = layout.height * scale
        const ctx = canvas.getContext('2d')!
        ctx.scale(scale, scale)
        ctx.drawImage(img, 0, 0)
        canvas.toBlob((blob) => blob && save(blob, 'png'), 'image/png')
        URL.revokeObjectURL(url)
      }
      img.src = url
    },
    [layout, colours, plan.data],
  )

  /* ------------------------------------------------------------- pointer */

  const pan = useRef<{ x: number; y: number; vx: number; vy: number } | null>(null)
  const drag = useRef<{ key: string; sx: number; sy: number; moving: boolean } | null>(null)
  const [ghost, setGhost] = useState<
    { key: string; x: number; y: number; over: string | null; dx: number; dy: number } | null
  >(null)

  const toMap = useCallback(
    (clientX: number, clientY: number) => {
      const rect = canvasRef.current!.getBoundingClientRect()
      return { x: (clientX - rect.left - view.x) / view.k, y: (clientY - rect.top - view.y) / view.k }
    },
    [view],
  )

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      if (pan.current) {
        const p = pan.current
        setView((v) => ({ ...v, x: p.vx + (e.clientX - p.x), y: p.vy + (e.clientY - p.y) }))
        return
      }
      const d = drag.current
      if (!d) return
      if (!d.moving && Math.hypot(e.clientX - d.sx, e.clientY - d.sy) < 6) return
      d.moving = true
      const at = toMap(e.clientX, e.clientY)
      const over = dropTarget(layout, at.x, at.y, d.key)
      setGhost({
        key: d.key,
        x: at.x,
        y: at.y,
        over: over?.key ?? null,
        // What the topic would move by if it were let go here. Drawn live, so the map
        // shows the arrangement rather than a label following the cursor.
        dx: (e.clientX - d.sx) / view.k,
        dy: (e.clientY - d.sy) / view.k,
      })
    }
    const onUp = (e: PointerEvent) => {
      pan.current = null
      const d = drag.current
      drag.current = null
      if (!d) return
      if (!d.moving) {
        setSelected(d.key)
        canvasRef.current?.focus()
        setGhost(null)
        return
      }
      const at = toMap(e.clientX, e.clientY)
      const over = dropTarget(layout, at.x, at.y, d.key)
      setGhost(null)
      const current = layout.byKey.get(d.key)
      /*
        REPARENTING IS ARRANGING OUR PLAN, so it asks both: `mayAdd` — the plan's shape is
        ours — and `editable` on the row being dragged, because moving a task you may not edit
        under a different phase is editing it. Dragging is not a quieter way to do either than
        typing.

        Dropping on open canvas is the same question again: the offset is stored per PROJECT,
        so it is our map being rearranged, not a private view.
      */
      if (over && mayAdd && layout.byKey.get(d.key)?.row?.editable && over.key !== current?.parent) {
        // Dropped on another topic: that is a change to the plan, not to the drawing.
        void moveUnder(d.key, over.key).catch(() => {})
        return
      }
      /* Saved against the PROJECT, so nudging the map is rearranging it for everybody. */
      if (!over && mayAdd) {
        // Dropped on open canvas: leave it where it was put. The offset is from where
        // the layout would have placed it, so the arrangement survives a longer title.
        const was = arrangement.offsets.get(d.key) ?? { dx: 0, dy: 0 }
        arrangement.place(d.key, was.dx + (e.clientX - d.sx) / view.k, was.dy + (e.clientY - d.sy) / view.k)
      }
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onUp)
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onUp)
    }
  }, [layout, toMap, moveUnder, writable, mayAdd, view.k, arrangement])

  /* ------------------------------------------------------------- keyboard */

  const onKey = (e: React.KeyboardEvent) => {
    if (editing) return
    const mod = e.metaKey || e.ctrlKey
    if (mod && e.key.toLowerCase() === 'z') {
      e.preventDefault()
      if (e.shiftKey) void redoLast().catch(() => {})
      else void undoLast().catch(() => {})
      return
    }
    if (mod && e.key.toLowerCase() === 'y') {
      e.preventDefault()
      void redoLast().catch(() => {})
      return
    }
    if (e.key === '+' || e.key === '=') return zoomBy(1.2)
    if (e.key === '-') return zoomBy(1 / 1.2)
    if (e.key === '0') return fit()
    if (e.key === 'Escape') {
      setSelected(null)
      setMenu(null)
      return
    }
    if (!selected) {
      if (e.key.startsWith('Arrow')) setSelected(layout.root)
      return
    }
    const node = layout.byKey.get(selected)
    if (!node) return
    switch (e.key) {
      case 'ArrowUp':
      case 'ArrowDown':
      case 'ArrowLeft':
      case 'ArrowRight': {
        e.preventDefault()
        const next = neighbour(layout, selected, e.key.slice(5).toLowerCase() as 'up' | 'down' | 'left' | 'right')
        if (next) setSelected(next)
        return
      }
      case 'Enter':
        e.preventDefault()
        if (mayAdd) void addSibling(selected).catch(() => {})
        return
      case 'Tab':
        e.preventDefault()
        if (!mayAdd) return
        if (node.row?.type_key === 'subtask') {
          toast('A sub-task is the bottom of the outline. Press Enter to add beside it.', 'info')
          return
        }
        void addChild(selected).catch(() => {})
        return
      case 'F2':
        e.preventDefault()
        /* The same question the menu's Rename asks: this row, not the screen. */
        if (writable && node.row?.editable) setEditing(selected)
        return
      case ' ':
        e.preventDefault()
        if (node.children.length || node.hidden) toggle(selected)
        return
      case 'Delete':
      case 'Backspace':
        e.preventDefault()
        /*
          `may_delete`, not `writable`. The key was the way around the menu: whoever could not
          see Delete could still press Del and have the API refuse it, which reads as a bug.
        */
        if (node.row?.may_delete) remove(selected)
        return
    }
  }

  /* ------------------------------------------------------------- render */

  if (plan.error) return <ErrorNote error={plan.error} />
  if (plan.isLoading) {
    return (
      <div className="grid place-items-center py-20">
        <Spinner />
      </div>
    )
  }
  if (!plan.data) return null

  const selectedNode = selected ? layout.byKey.get(selected) : undefined
  const selectedRow = selectedNode?.row ?? null
  /* `may_delete` on the row itself: `work.delete`, which no client role holds. */
  const canDelete = Boolean(
    selectedRow?.may_delete && !selectedNode?.children.length && !selectedNode?.hidden,
  )
  /* And the tick is `work.move` — finishing a task is moving it to Done. */
  const canTick = Boolean(selectedRow?.may_move && !selectedRow.has_children)

  return (
    <div className="flex flex-col gap-3 md:h-[calc(100dvh-var(--shell-header)-3rem)]">
      <PageHeader
        title="Mind map"
        subtitle="The plan from the middle out. Enter adds a topic beside the one you are on, Tab one under it; drag a topic where you want it, or onto another topic to move it there."
      />

      <div className="flex flex-wrap items-center gap-1">
        <Tool label="Zoom out  (−)" onClick={() => zoomBy(1 / 1.2)}>
          <ZoomOutIcon />
        </Tool>
        <span className="min-w-[44px] text-center font-mono text-[11px] text-[var(--color-ink-dim)]">
          {Math.round(view.k * 100)}%
        </span>
        <Tool label="Zoom in  (+)" onClick={() => zoomBy(1.2)}>
          <ZoomInIcon />
        </Tool>
        <Tool label="Fit the whole map in the window  (0)" onClick={fit}>
          <FitIcon />
        </Tool>
        <Divider />
        {/*
          BOTH OF THESE FOLD AND UNFOLD PHASES, SO BOTH ARE DEAD ON A PLAN THAT HAS NONE.
          
          They looked broken, and the reason was worth finding: a flat plan — every topic a task
          hanging off the project, no phase anywhere — gives "Phases only" an empty set to
          collapse and "Expand all" nothing to open. Both ran correctly and changed nothing on
          screen, which is indistinguishable from a button that does not work.
          
          Disabled, with the reason in the tooltip, so the answer is on the button instead of
          being something you have to work out.
        */}
        <Tip label={folds.length === 0 ? 'Nothing is folded' : 'Open every phase'}>
          <Button size="sm" disabled={folds.length === 0} onClick={() => setCollapsed(() => new Set())}>
            Expand all
          </Button>
        </Tip>
        <Tip
          label={
            phaseKeys.length === 0
              ? 'This plan has no phases — every topic is a task in its own right. Indent one under another to make a phase.'
              : 'Fold every phase, so only the outline shows'
          }
        >
          <Button
            size="sm"
            disabled={phaseKeys.length === 0}
            onClick={() => setCollapsed(() => new Set(phaseKeys))}
          >
            Phases only
          </Button>
        </Tip>
        {writable && (
          <>
            <Divider />
            {/* Adding is ours — see `mayAdd`. Editing what is already here is not the same act. */}
            {mayAdd && (
              <>
                <Tool
                  label={
                    selectedRow?.type_key === 'subtask'
                      ? 'A sub-task is the bottom of the outline — add beside it instead'
                      : 'Add a topic under this one  (Tab)'
                  }
                  onClick={() => selected && void addChild(selected).catch(() => {})}
                  disabled={!selected || selectedRow?.type_key === 'subtask' || (!!selectedRow && !selectedRow.editable)}
                >
                  <AddIcon />
                </Tool>
                <Tool
                  label="Add a topic beside this one  (Enter)"
                  onClick={() => selected && void addSibling(selected).catch(() => {})}
                  disabled={!selectedRow}
                >
                  <InsertBelowIcon />
                </Tool>
              </>
            )}
            {mayTick && (
              <Tool
                label={selectedRow?.status_category === 'done' ? 'Reopen' : 'Mark done'}
                onClick={() => selected && tick(selected)}
                disabled={!canTick}
                active={selectedRow?.status_category === 'done'}
              >
                <TickIcon />
              </Tool>
            )}
            {mayDelete && (
              <Tool label="Delete this topic  (Del)" onClick={() => selected && remove(selected)} disabled={!canDelete} danger>
                <TrashIcon />
              </Tool>
            )}
            {/*
              Undo and redo walk back what this screen did. A customer can no longer do anything
              here for them to walk back, so they would sit disabled forever — and a control that
              can never come alive is not an affordance.
            */}
            {mayAdd && (
              <>
                <Divider />
                <Tool label="Undo  (⌘Z)" onClick={() => void undoLast().catch(() => {})} disabled={!history.length}>
                  <UndoIcon />
                </Tool>
                <Tool label="Redo  (⇧⌘Z)" onClick={() => void redoLast().catch(() => {})} disabled={!redoStack.length}>
                  <RedoIcon />
                </Tool>
              </>
            )}
          </>
        )}
        <Divider />
        <Tool label="Download the map as an SVG drawing" onClick={() => void exportMap('svg')}>
          <ExportIcon />
        </Tool>
        <Button size="sm" onClick={() => void exportMap('png')}>
          PNG
        </Button>
        {mayAdd && arrangement.offsets.size > 0 && (
          <Button
            size="sm"
            title="Forget where topics were dragged and let the layout arrange them again"
            onClick={() => arrangement.tidy()}
          >
            Tidy up
          </Button>
        )}
        {/*
          ONE TASK EDITOR, AND IT IS THE TASK'S OWN.
          
          This was a "Properties" panel of the map's own, with its own smaller set of fields —
          a single Owner where a task holds several people, a typed percentage, a milestone
          toggle. Three of those disagreed with the task itself, and the assignee one silently
          deleted people. A second editor over one row is a promise to keep two things in step
          that nothing enforces, so it is gone: this opens the same drawer the board opens, with
          the status, the stage, the people, the description and the rest.
        */}
        <Button
          size="sm"
          disabled={!selectedRow}
          title={selectedRow ? `Open ${selectedRow.key}` : 'Pick a topic first'}
          onClick={() => selectedRow && onOpenTask?.(selectedRow.id)}
        >
          Open task
        </Button>
        {/*
          CLOSED work is hidden by default — not finished work. A task that is done but still on
          the board stays on the map: it is what the branches after it were waiting for, and
          taking it off leaves a gap that reads as missing rather than finished.
          
          Same word and same toggle as the board's "show closed", so the two mean one thing.
        */}
        <Button
          size="sm"
          variant={showClosed ? 'secondary' : 'ghost'}
          aria-pressed={showClosed}
          title={
            showClosed
              ? 'Hide work that has been closed and filed away'
              : 'Show work that has been closed and filed away as well'
          }
          onClick={() => setShowClosed(!showClosed)}
        >
          {showClosed ? 'Hide closed' : 'Show closed'}
        </Button>
        <span className="ml-auto text-[12px] text-[var(--color-ink-dim)]">
          {selectedNode ? (
            <>
              {selectedRow ? <span className="font-mono">{selectedRow.key}</span> : 'The project'} · {selectedNode.title}
            </>
          ) : (
            'Click a topic · drag it where you want it · drop it on another to move it there · wheel zooms'
          )}
        </span>
      </div>

      {/* An empty plan is still a map: the project in the middle, and Tab grows the
          first branch. The screen used to send people to the Gantt to add the first
          task, which is exactly backwards for the tool built for starting a plan. */}
      <div className="flex min-h-0 flex-1 gap-3">
        <div ref={cardRef} className="card relative min-h-[360px] min-w-0 flex-1 overflow-hidden">
          <div
            ref={canvasRef}
            tabIndex={0}
            role="application"
            aria-label="Mind map of the plan"
            className={clsx(
              'absolute inset-0 outline-none select-none',
              ghost ? 'cursor-grabbing' : 'cursor-grab',
            )}
            onKeyDown={onKey}
            onWheel={(e) => {
              e.preventDefault()
              const rect = canvasRef.current!.getBoundingClientRect()
              zoomBy(e.deltaY < 0 ? 1.1 : 1 / 1.1, { x: e.clientX - rect.left, y: e.clientY - rect.top })
            }}
            onPointerDown={(e) => {
              if (e.button !== 0) return
              if ((e.target as HTMLElement).closest('[data-topic]')) return
              pan.current = { x: e.clientX, y: e.clientY, vx: view.x, vy: view.y }
              setSelected(null)
              canvasRef.current?.focus()
            }}
          >
            <div
              className="absolute top-0 left-0 origin-top-left"
              style={{ transform: `translate(${view.x}px, ${view.y}px) scale(${view.k})`, width: layout.width, height: layout.height }}
            >
              <svg className="pointer-events-none absolute inset-0 overflow-visible" width={layout.width} height={layout.height}>
                {layout.nodes.map((n) => {
                  if (!n.parent) return null
                  const parent = layout.byKey.get(n.parent)!
                  const colour = colours.get(n.key) ?? 'var(--color-rule-strong)'
                  return (
                    <path
                      key={n.key}
                      d={connector(parent, n)}
                      fill="none"
                      stroke={colour}
                      strokeWidth={n.depth === 1 ? 2 : 1.4}
                      strokeDasharray={n.loose ? '5 4' : undefined}
                      opacity={n.row?.status_category === 'done' ? 0.45 : 0.85}
                    />
                  )
                })}
              </svg>

              {layout.nodes.map((n) => (
                <Topic
                  key={n.key}
                  node={n}
                  colour={colours.get(n.key) ?? 'var(--gantt-epic)'}
                  selected={selected === n.key}
                  editing={editing === n.key}
                  dropping={ghost?.over === n.key}
                  dragging={ghost?.key === n.key}
                  // While it is being dragged across open canvas the topic itself moves,
                  // rather than a label trailing the pointer: the map shows where the
                  // arrangement will be before the button is let go.
                  shift={
                    ghost && ghost.over === null && (ghost.key === n.key || descendants(layout, ghost.key).includes(n.key))
                      ? { dx: ghost.dx, dy: ghost.dy }
                      : null
                  }
                  onPointerDown={(e) => {
                    if (e.button !== 0) return
                    e.stopPropagation()
                    drag.current = { key: n.key, sx: e.clientX, sy: e.clientY, moving: false }
                  }}
                  onDoubleClick={() => writable && n.row?.editable && setEditing(n.key)}
                  onContextMenu={(e) => {
                    e.preventDefault()
                    setSelected(n.key)
                    setMenu({ key: n.key, x: e.clientX, y: e.clientY })
                  }}
                  onToggle={() => toggle(n.key)}
                  onRename={(title) => void rename(n.key, title).catch(() => {})}
                  onCancel={() => {
                    setEditing(null)
                    canvasRef.current?.focus()
                  }}
                />
              ))}

              {ghost && ghost.over === null && (
                <div
                  className="pointer-events-none absolute rounded-[7px] border border-dashed border-[var(--color-accent)] bg-[var(--color-accent-soft)]/70 px-2 py-1 text-[12px]"
                  style={{ left: ghost.x + 12, top: ghost.y + 12 }}
                >
                  {layout.byKey.get(ghost.key)?.title}
                  {descendants(layout, ghost.key).length ? ` +${descendants(layout, ghost.key).length}` : ''}
                </div>
              )}
            </div>
          </div>

          <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-center gap-4 border-t border-[var(--color-rule)] bg-[var(--color-surface)]/90 px-3 py-1.5 text-[11px] text-[var(--color-ink-dim)]">
            <span>{rows.length} topics · {rows.filter((r) => r.status_category === 'done').length} done</span>
            <span>Enter sibling · Tab child · F2 rename · Space fold · Del delete · arrows move</span>
            {/* The Gantt is the board's Timeline tab here, not a route of its own — the kit's
                own `/p/{key}/schedule/gantt` does not exist in this app and landed on the
                catch-all. */}
            <Link
              to={`/projects/${plan.data.project.id}/board?view=timeline`}
              className="pointer-events-auto ml-auto underline"
            >
              The same plan on the Gantt
            </Link>
          </div>
        </div>
      </div>

      {menu && (
        <ContextMenu
          at={menu}
          onClose={() => setMenu(null)}
          entries={menuEntries({
            node: layout.byKey.get(menu.key),
            writable,
            mayAdd,
            onAddChild: () => void addChild(menu.key).catch(() => {}),
            onAddSibling: () => void addSibling(menu.key).catch(() => {}),
            onRename: () => setEditing(menu.key),
            onToggle: () => toggle(menu.key),
            onTick: () => tick(menu.key),
            onOpen: () => {
              const r = rowOf(menu.key)
              if (r) onOpenTask?.(r.id)
            },
            onDelete: () => remove(menu.key),
          })}
        />
      )}
    </div>
  )
}

function menuEntries(a: {
  node: MindNode | undefined
  /** Whether this plan can be edited at all — the shape of the screen, not of a row. */
  writable: boolean
  /** Whether NEW topics may be added, which is asked by side rather than by capability. */
  mayAdd: boolean
  onAddChild: () => void
  onAddSibling: () => void
  onRename: () => void
  onToggle: () => void
  onTick: () => void
  onOpen: () => void
  onDelete: () => void
}): MenuEntry[] {
  const n = a.node
  if (!n) return []
  /**
   * ASKED OF THE ROW, NOT OF THE SCREEN.
   *
   * Every entry below used to hang off one `writable`. Renaming answers to `work.write` and to
   * whether the row is ours to change; finishing answers to `work.move`; deleting answers to
   * `work.delete`. The API has always judged them separately — this menu now asks the same three
   * questions rather than one, so nothing is offered that the save will refuse.
   *
   * The root topic has no row, so nothing may be done to it but folding.
   */
  const mayEdit = !!n.row?.editable
  const entries: MenuEntry[] = [{ kind: 'heading', label: n.row ? `${n.row.key}` : 'The project' }]
  if (a.mayAdd) {
    entries.push(
      {
        kind: 'item',
        label: 'Add a topic under this',
        shortcut: 'Tab',
        icon: <AddIcon />,
        /* A locked row cannot take new work either: adding under it is changing what is agreed. */
        disabled: n.row?.type_key === 'subtask' || (!!n.row && !mayEdit),
        onSelect: a.onAddChild,
      },
      { kind: 'item', label: 'Add a topic beside this', shortcut: 'Enter', icon: <InsertBelowIcon />, disabled: !n.row, onSelect: a.onAddSibling },
    )
  }
  if (a.writable) {
    entries.push(
      { kind: 'item', label: 'Rename', shortcut: 'F2', disabled: !n.row || !mayEdit, onSelect: a.onRename },
    )
  }
  if (a.mayAdd || a.writable) entries.push({ kind: 'divider' })
  if (n.children.length || n.hidden) {
    entries.push({ kind: 'item', label: n.collapsed ? `Unfold (${n.hidden} hidden)` : 'Fold this branch', shortcut: 'Space', onSelect: a.onToggle })
  }
  if (n.row) {
    /* `work.move`, because finishing a task is moving it to Done. No client role holds it. */
    if (n.row.may_move && !n.row.has_children) {
      entries.push({
        kind: 'item',
        label: n.row.status_category === 'done' ? 'Reopen' : 'Mark done',
        icon: <TickIcon />,
        onSelect: a.onTick,
      })
    }
    entries.push({ kind: 'item', label: 'Open the task', icon: <OpenIcon />, onSelect: a.onOpen })
    /*
      LEFT OUT ENTIRELY rather than shown greyed. A disabled Delete invites the question "why
      not", and the honest answer — that deleting is not something a customer does here — is not
      something a menu row can carry. Whoever holds it sees it; whoever does not never had it.
    */
    if (n.row.may_delete) {
      entries.push(
        { kind: 'divider' },
        {
          kind: 'item',
          label: 'Delete',
          shortcut: 'Del',
          icon: <TrashIcon />,
          danger: true,
          disabled: Boolean(n.children.length || n.hidden),
          onSelect: a.onDelete,
        },
      )
    }
  }
  return entries
}

/* ------------------------------------------------------------------ one topic */

function Topic({
  node: n,
  colour,
  selected,
  editing,
  dropping,
  dragging,
  onPointerDown,
  onDoubleClick,
  onContextMenu,
  onToggle,
  onRename,
  onCancel,
  shift,
}: {
  node: MindNode
  colour: string
  selected: boolean
  editing: boolean
  dropping: boolean
  dragging: boolean
  onPointerDown: (e: React.PointerEvent) => void
  onDoubleClick: () => void
  onContextMenu: (e: React.MouseEvent) => void
  onToggle: () => void
  onRename: (title: string) => void
  onCancel: () => void
  shift?: { dx: number; dy: number } | null
}) {
  const s = sizeFor(n.depth)
  const row = n.row
  const done = row?.status_category === 'done'
  const soft = colour.startsWith('#') ? `${colour}22` : 'var(--color-surface-sunk)'
  const hasKids = n.children.length > 0 || n.hidden > 0
  const left = n.cx - n.w / 2 + (shift?.dx ?? 0)
  const top = n.cy - n.h / 2 + (shift?.dy ?? 0)

  return (
    <div
      data-topic={n.key}
      className={clsx('absolute', dragging && !shift && 'opacity-40', shift && 'z-[5] shadow-lg')}
      style={{ left, top, width: n.w, height: n.h }}
      onPointerDown={onPointerDown}
      onDoubleClick={onDoubleClick}
      onContextMenu={onContextMenu}
    >
      <div
        className={clsx(
          'flex h-full w-full items-center gap-1.5 rounded-[7px] border px-2.5 text-left transition-shadow',
          n.depth === 0 && 'rounded-[10px] border-transparent bg-[var(--color-ink)] text-[var(--color-surface)]',
          n.depth === 1 && 'font-semibold text-[var(--color-ink)]',
          n.depth > 1 && 'bg-[var(--color-surface)] text-[var(--color-ink)]',
          selected && 'ring-2 ring-[var(--color-accent)] ring-offset-1 ring-offset-[var(--color-surface)]',
          dropping && 'ring-2 ring-[var(--color-success)] ring-offset-1',
          row?.late && !done && 'border-[var(--color-danger)]',
          done && n.depth > 0 && 'opacity-70',
        )}
        style={{
          fontSize: s.font,
          borderColor: n.depth === 0 ? undefined : colour,
          background: n.depth === 1 ? soft : undefined,
          borderWidth: n.depth === 1 ? 1.5 : undefined,
        }}
        title={row ? `${row.key} · ${row.title}${n.loose ? ' · at the top level of the plan' : ''}` : n.title}
      >
        {row?.is_milestone && <span aria-hidden className="text-[10px]" style={{ color: colour }}>◆</span>}
        {editing ? (
          <input
            ref={selectOnFocus}
            defaultValue={n.title}
            className="min-w-0 flex-1 bg-transparent outline-none"
            style={{ fontSize: s.font }}
            onPointerDown={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              e.stopPropagation()
              if (e.key === 'Enter') onRename((e.target as HTMLInputElement).value)
              if (e.key === 'Escape') onCancel()
            }}
            onBlur={(e) => onRename(e.target.value)}
          />
        ) : (
          <span className={clsx('min-w-0 flex-1 truncate', done && n.depth > 0 && 'line-through')}>{n.title}</span>
        )}
        {row && n.depth > 0 && row.progress_pct !== null && !editing && (
          <span className="shrink-0 font-mono text-[10px] font-normal tabular-nums text-[var(--color-ink-dim)]">
            {Math.round(row.progress_pct)}%
          </span>
        )}
      </div>

      {hasKids && n.depth > 0 && (
        <button
          type="button"
          aria-label={n.collapsed ? `Unfold ${n.title}` : `Fold ${n.title}`}
          title={n.collapsed ? `${n.hidden} hidden — click to unfold` : 'Fold this branch'}
          className={clsx(
            'absolute top-1/2 flex size-[18px] -translate-y-1/2 items-center justify-center rounded-full border bg-[var(--color-surface)] font-mono text-[10px] leading-none text-[var(--color-ink)] shadow-sm hover:bg-[var(--color-surface-sunk)]',
            n.side === 'right' ? '-right-[26px]' : '-left-[26px]',
          )}
          style={{ borderColor: colour }}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation()
            onToggle()
          }}
        >
          {n.collapsed ? n.hidden : '−'}
        </button>
      )}
    </div>
  )
}

export type { Row }

/**
 * Where the topics have been put, and how to put one somewhere else.
 *
 * Positions live on the server rather than in this browser: a map is arranged to be
 * looked at by other people, and an arrangement only one laptop can see is a drawing
 * nobody else has.
 */
function useArrangement(projectId: string | undefined) {
  const qc = useQueryClient()
  const key = ['mind-map', projectId]
  const saved = useQuery({
    queryKey: key,
    enabled: Boolean(projectId),
    queryFn: () => api.get<{ key: string; dx: number; dy: number }[]>(`/projects/${projectId}/schedule/mind-map`),
    staleTime: 30_000,
  })

  const offsets = useMemo(() => {
    const out: Offsets = new Map()
    for (const row of saved.data ?? []) out.set(row.key, { dx: row.dx, dy: row.dy })
    return out
  }, [saved.data])

  const move = useMutation({
    mutationFn: (input: { key: string; dx: number; dy: number }) =>
      api.put(`/projects/${projectId}/schedule/mind-map`, input),
    // Drawn immediately, corrected by the server's answer. A topic that snapped back to
    // where it started while the request was in flight would read as a refusal.
    onMutate: (input) => {
      qc.setQueryData<{ key: string; dx: number; dy: number }[]>(key, (old) => [
        ...(old ?? []).filter((r) => r.key !== input.key),
        input,
      ])
    },
    onSettled: () => qc.invalidateQueries({ queryKey: key }),
  })

  const clear = useMutation({
    mutationFn: () => api.del(`/projects/${projectId}/schedule/mind-map`),
    onSuccess: () => qc.setQueryData(key, []),
    onSettled: () => qc.invalidateQueries({ queryKey: key }),
  })

  return {
    offsets,
    place: (topic: string, dx: number, dy: number) => move.mutate({ key: topic, dx, dy }),
    tidy: () => clear.mutate(),
  }
}
